import { useCallback, useEffect, useState } from 'react';
import { MdCloudUpload, MdContentCopy, MdLink, MdLinkOff, MdRefresh } from 'react-icons/md';
import { Drawer, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button, Field } from '../ui';
import { icsLink, publishGist, regenerateIcsLink, revokeIcsLink, unpublishGist, updateCalendar } from '../../api/calendar';
import { messaggioErrore } from '../../utils/apiError';

/** Nome, colore e link ICS di un calendario. Il link si attiva solo se lo chiedi. */
export default function CalendarSettings({ calendar, open, onClose, onChanged }) {
  const [bozza, setBozza] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gist, setGist] = useState(null);
  const [copiato, setCopiato] = useState(false);
  const [errore, setErrore] = useState('');

  const proprietario = calendar?.role === 'owner';

  const caricaLink = useCallback(async () => {
    if (!calendar || !proprietario) return;
    try { setLink((await icsLink(calendar.id)).url); }
    catch (e) { setErrore(messaggioErrore(e)); }
  }, [calendar, proprietario]);

  useEffect(() => {
    setErrore('');
    if (open && calendar) {
      setBozza({ name: calendar.name, color: calendar.color });
      setGist(calendar.gist_url || null);
      setCopiato(false);
      caricaLink();
    }
  }, [open, calendar, caricaLink]);

  if (!calendar) return null;

  const esegui = async (azione) => {
    setBusy(true); setErrore('');
    try { await azione(); } catch (e) { setErrore(messaggioErrore(e)); } finally { setBusy(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} label={`Impostazioni: ${calendar.name}`}
      description={proprietario ? 'Sei il proprietario' : 'Condiviso con te'}>
      <DialogBody className="space-y-4">
        {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

        <Field label="Nome" value={bozza?.name || ''} disabled={!proprietario}
          onChange={e => setBozza({ ...bozza, name: e.target.value })} />
        <label className="flex items-center gap-2 text-sm text-ink">
          Colore
          <input type="color" aria-label="Colore del calendario" disabled={!proprietario}
            value={bozza?.color || '#16a34a'}
            onChange={e => setBozza({ ...bozza, color: e.target.value })} />
        </label>

        {proprietario && (
          <section className="space-y-2 border-t border-line pt-3">
            <h3 className="text-sm font-semibold text-ink">Iscrizione dal telefono</h3>
            <p className="text-xs text-muted">
              Pubblica il calendario su GitHub: l&apos;indirizzo che ottieni si incolla in
              Google Calendar o nel calendario dell&apos;iPhone, e si aggiorna da solo a ogni
              modifica. <strong>Chi ha il link legge il calendario</strong>: non compare negli
              elenchi e non si trova cercando, ma non è protetto da password.
            </p>
            {gist && (
              <div className="flex gap-2">
                <input readOnly aria-label="Indirizzo di iscrizione" value={gist}
                  onFocus={e => e.target.select()}
                  className="flex-1 rounded border border-line bg-surface-2 px-2 py-2 text-xs" />
                <Button size="sm" icon={MdContentCopy}
                  onClick={() => { navigator.clipboard?.writeText(gist); setCopiato(true); }}>
                  {copiato ? 'Copiato' : 'Copia'}
                </Button>
              </div>
            )}
            {calendar.gist_error && (
              <p role="alert" className="rounded bg-danger-bg px-2 py-1 text-xs text-danger">{calendar.gist_error}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={gist ? 'secondary' : 'primary'} icon={MdCloudUpload} loading={busy}
                onClick={() => esegui(async () => {
                  setGist((await publishGist(calendar.id)).gist_url);
                  await onChanged?.();
                })}>
                {gist ? 'Aggiorna ora' : 'Pubblica e ottieni il link'}
              </Button>
              {gist && (
                <Button size="sm" variant="danger-ghost" icon={MdLinkOff} loading={busy}
                  onClick={() => esegui(async () => {
                    await unpublishGist(calendar.id); setGist(null); await onChanged?.();
                  })}>
                  Ritira la pubblicazione
                </Button>
              )}
            </div>
            {gist && (
              <p className="text-2xs text-muted">
                Ultimo aggiornamento: {calendar.gist_synced_at
                  ? new Date(calendar.gist_synced_at).toLocaleString('it-IT') : 'in corso'}.
                Si riallinea da solo quando modifichi un evento.
              </p>
            )}
          </section>
        )}

        {proprietario && (
          <section className="space-y-2 border-t border-line pt-3">
            <h3 className="text-sm font-semibold text-ink">Link ICS locale</h3>
            <p className="text-xs text-muted">
              Funziona solo da questo computer: è servito da Wozzap su localhost. Per il telefono
              usa la pubblicazione qui sopra.
            </p>
            {link && (
              <input readOnly aria-label="Link ICS" value={link} onFocus={e => e.target.select()}
                className="w-full rounded border border-line bg-surface-2 px-2 py-2 text-xs" />
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" icon={link ? MdRefresh : MdLink} loading={busy}
                onClick={() => esegui(async () => setLink((await regenerateIcsLink(calendar.id)).url))}>
                {link ? 'Rigenera (revoca il precedente)' : 'Attiva link ICS'}
              </Button>
              {link && (
                <Button size="sm" variant="danger-ghost" icon={MdLinkOff} loading={busy}
                  onClick={() => esegui(async () => { await revokeIcsLink(calendar.id); setLink(null); })}>
                  Revoca
                </Button>
              )}
            </div>
          </section>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Chiudi</Button>
        {proprietario && (
          <Button variant="primary" loading={busy} disabled={!bozza?.name?.trim()}
            onClick={() => esegui(async () => {
              await updateCalendar(calendar.id, bozza);
              await onChanged?.();
              onClose();
            })}>
            Salva
          </Button>
        )}
      </DialogFooter>
    </Drawer>
  );
}
